import { Skeleton } from "@credit-acceptance/uikit";
import { InfoIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components/ui/button";
import { EmptyState, ErrorState } from "../../../../components/ui/centeredMessage";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components/ui/tooltip";
import { useAuth } from "../../../../hooks/useAuth";
import { useRolePermissions } from "../../../../hooks/useRolePermissions";
import { descriptionsForCodes } from "../../../../lib/permissions/dealState/dealStatusCatalog";
import { getErrorMessage } from "../../../../lib/query/errors";
import { useActionPanelStore } from "../../../../stores/actionPanelStore";
import { useApplicationStatus } from "../../api/useApplicationStatus";
import Section from "../../components/Section";
import { openDmsAdjustmentSheet } from "./api/openDmsAdjustmentSheet";
import { useDealStatus } from "./api/useDealStatus";
import FinancialBreakdown from "./components/FinancialBreakdown";
import MilestoneDates from "./components/MilestoneDates";
import ReassignmentModal, { type ReassignmentStatusCode } from "./ReassignmentModal";

const REASSIGNMENT_STATUS_CODES: readonly ReassignmentStatusCode[] = ["RAR", "RAP", "RAC"];

const smallTextClassName =
  "text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)";

interface DealStatusSectionProperties {
  approvalId: string;
  canViewInternalHistory?: boolean;
  canViewPreApprovalHistory?: boolean;
}

function DealStatusSection({
  approvalId,
  canViewInternalHistory = false,
  canViewPreApprovalHistory = false,
}: DealStatusSectionProperties) {
  const { t } = useTranslation();
  const openInternalAudit = useActionPanelStore((state) => state.openInternalAudit);
  const { data, isLoading, isError, error } = useDealStatus(approvalId);
  const { user } = useAuth();
  const { capabilities } = useRolePermissions();
  const { data: applicationStatus } = useApplicationStatus(approvalId);
  const [reassignmentOpen, setReassignmentOpen] = useState(false);
  // CAC users only, and only while the deal is Re-assignment Request/Pending/Completed (mirrors caps2's iscacuser check).
  const reassignmentStatusCode = REASSIGNMENT_STATUS_CODES.find((code) =>
    descriptionsForCodes(code).has(applicationStatus?.statusDescription ?? "")
  );
  const showReassignmentLink = capabilities.isCacUser && reassignmentStatusCode !== undefined;
  const showCapsHistoryLink = canViewInternalHistory && data?.flags?.hasDealStatus === true;
  const showPreApprovalHistoryLink =
    canViewInternalHistory && canViewPreApprovalHistory && data?.flags?.hasDealStatus === false;

  const isEmpty = !data?.dealStatus;
  const programType = data?.flags?.currentProgramType?.trim();
  const showDealerCostDetail = data?.flags?.anyAncillaryVendorTpap === false;
  const isPurchaseProgram = data?.flags?.purchaseProgram === true;

  return (
    <Section title={t("dealStatus.sectionTitle")} scrollSpyCategory="Status">
      {isLoading && (
        <div role="status" aria-label={t("dealStatus.loading")}>
          <Skeleton loading active paragraph={{ rows: 6 }} />
        </div>
      )}

      {isError && (
        <ErrorState message={t("dealStatus.loadError", { message: getErrorMessage(error) })} className="p-0" />
      )}

      {!isLoading && !isError && isEmpty && <EmptyState message={t("dealStatus.empty")} className="p-0" />}

      {!isLoading && !isError && data && (
        <>
          {!isEmpty && (
            <>
              {programType && (
                <h3 className="mb-(--spacing-3) text-foreground text-sm font-semibold underline underline-offset-4">
                  {programType}
                </h3>
              )}
              <div className="gap-x-(--spacing-8) gap-y-(--spacing-4) grid grid-cols-1 sm:grid-cols-2">
                <FinancialBreakdown data={data} />
                <MilestoneDates dates={data.dealStatus?.milestoneDates} />
              </div>

              <div
                className={`border-border mt-(--spacing-4) pt-(--spacing-3) ${smallTextClassName} gap-(--spacing-1) flex flex-col border-t`}
              >
                <p>{t(isPurchaseProgram ? "dealStatus.purchasePriceFootnote" : "dealStatus.advanceFootnote")}</p>
                <p>
                  {t(isPurchaseProgram ? "dealStatus.amountPaidPurchaseFootnote" : "dealStatus.amountPaidFootnote")}
                </p>
              </div>
            </>
          )}

          <div className="mt-(--spacing-2) gap-x-(--spacing-4) gap-y-(--spacing-1) flex flex-wrap items-center">
            {showCapsHistoryLink && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => openInternalAudit("status-history")}
                    className={`gap-(--spacing-1) ${smallTextClassName} h-auto p-0 underline`}
                  >
                    {t("dealStatus.capsHistoryLink")}
                    <InfoIcon className="size-3.5" aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">{t("dealStatus.changeHistoryTooltip")}</TooltipContent>
              </Tooltip>
            )}
            {showPreApprovalHistoryLink && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => openInternalAudit("pre-approval-history")}
                className={`gap-(--spacing-1) ${smallTextClassName} h-auto p-0 underline`}
              >
                {t("dealStatus.preApprovalHistoryLink")}
                <InfoIcon className="size-3.5" aria-hidden="true" />
              </Button>
            )}

            {/* DMS Adjustment Sheet — fail-closed: shown only when TPAP is known false */}
            {!isEmpty && showDealerCostDetail && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => openDmsAdjustmentSheet(approvalId)}
                className={`${smallTextClassName} h-auto p-0 underline`}
              >
                {t("dealStatus.dmsAdjustmentSheetLink")}
              </Button>
            )}

            {showReassignmentLink && (
              <>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => setReassignmentOpen(true)}
                  className={`${smallTextClassName} h-auto p-0 underline`}
                >
                  {t("dealStatus.reassignmentFormLink")}
                </Button>
                <ReassignmentModal
                  open={reassignmentOpen}
                  onOpenChange={setReassignmentOpen}
                  approvalId={approvalId}
                  statusCode={reassignmentStatusCode}
                  planType={data.dealStatus?.programType}
                  roleName={user?.userRole}
                />
              </>
            )}
          </div>
        </>
      )}
    </Section>
  );
}

export default DealStatusSection;
